'use client'

import { useMemo, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import { useInView } from 'react-intersection-observer'

import type { ChartConfig } from '~/src/components/ui/chart'
import { cn } from '~/src/lib/utils'
import { SCATTER_CHART_CONTAINER_CLASS } from './reportChartContainers'
import type { NormalizedScatterPoint } from './SeoReportPricePerformanceScatterChart'
import { renderSeoReportLimitedMarkdown } from './SeoReportLimitedMarkdown'
import { SeoReportTruncatedCopy } from './SeoReportTruncatedCopy'

// The chart body carries the whole recharts dependency, so it stays out of
// the page bundle and only downloads once the section nears the viewport
// (see the useInView gate below). The placeholder mirrors the chart
// container's dimensions so the swap doesn't shift layout.
const ScatterChartPlaceholder = () => (
  <div aria-hidden='true' className={SCATTER_CHART_CONTAINER_CLASS} />
)

const SeoReportPricePerformanceScatterChart = dynamic(
  () =>
    import('./SeoReportPricePerformanceScatterChart').then(
      (m) => m.SeoReportPricePerformanceScatterChart
    ),
  { ssr: false, loading: ScatterChartPlaceholder }
)

// color-token-exception: data-viz category colors for scatter plot points
const OTHER_CANDIDATE_COLOR = '#1EA6F0'
const TOP_PICK_COLOR = '#E14F0B'

export interface PricePerformanceScatterPoint {
  id: string
  name: string
  price: number
  metricValue: number
  pointLabel?: string
  tooltipLabel?: string
  tooltipDetail?: string
  color?: string
  size?: number
  isTopPick?: boolean
  isHighlighted?: boolean
}

export interface SeoReportPricePerformanceScatterSectionProps {
  title?: string
  description?: string
  summary?: ReactNode
  points: PricePerformanceScatterPoint[]
  className?: string
  xTicks?: number[]
  yTicks?: number[]
  xDomain?: [number, number]
  yDomain?: [number, number]
  yAxisLabel?: string
  formatMetricTick?: (value: number | string) => string
  formatMetricDetail?: (value: number) => string
  topPickLegendLabel?: string
  otherCandidatesLegendLabel?: string
  headingVariant?: 'default' | 'report'
}

function formatBatteryTick(value: number | string): string {
  if (typeof value !== 'number') return String(value)
  return `${value}h`
}

function formatBatteryDetail(value: number): string {
  return `${value}h battery`
}

export function SeoReportPricePerformanceScatterSection({
  title = 'Price vs. Battery Performance',
  description = 'Why pay more for less battery? See where our picks land.',
  summary,
  points,
  className,
  xTicks,
  yTicks,
  xDomain,
  yDomain,
  yAxisLabel = 'BATTERY (HRS) →',
  formatMetricTick = formatBatteryTick,
  formatMetricDetail = formatBatteryDetail,
  topPickLegendLabel = 'Top pick',
  otherCandidatesLegendLabel = 'Other candidates',
  headingVariant = 'default',
}: SeoReportPricePerformanceScatterSectionProps) {
  const { ref: chartInViewRef, inView: chartInView } = useInView({
    triggerOnce: true,
    rootMargin: '600px 0px',
  })
  const usesReportHeading = headingVariant === 'report'
  const HeadingTag = usesReportHeading ? 'h3' : 'h2'
  const normalizedPoints = useMemo<NormalizedScatterPoint[]>(
    () =>
      points.map((point) => ({
        ...point,
        color:
          point.color ??
          (point.isTopPick ? TOP_PICK_COLOR : OTHER_CANDIDATE_COLOR),
        size: point.size ?? (point.isTopPick ? 28 : 18),
      })),
    [points]
  )

  const chartConfig = useMemo<ChartConfig>(
    () => ({
      topPick: {
        label: topPickLegendLabel,
        color: TOP_PICK_COLOR,
      },
      otherCandidates: {
        label: otherCandidatesLegendLabel,
        color: OTHER_CANDIDATE_COLOR,
      },
    }),
    [otherCandidatesLegendLabel, topPickLegendLabel]
  )

  if (normalizedPoints.length === 0) {
    return null
  }

  return (
    <section className={cn('bg-white py-24', className)} aria-label={title}>
      <div
        className={cn(
          'mx-auto flex max-w-[1280px] flex-col',
          usesReportHeading ? 'w-full items-stretch' : 'items-center lg:px-8'
        )}
      >
        <div
          className={cn(
            'max-w-[768px]',
            usesReportHeading ? 'text-left' : 'text-center'
          )}
        >
          <HeadingTag
            className={
              usesReportHeading
                ? 'text-xl font-semibold leading-7 text-foreground'
                : 'text-[36px] font-semibold leading-[44px] tracking-[-0.02em] text-foreground-strong'
            }
          >
            {title}
          </HeadingTag>
          <p
            className={
              usesReportHeading
                ? 'mt-2 text-sm leading-6 text-foreground-secondary'
                : 'mt-5 text-[18px] leading-7 text-foreground-faint'
            }
          >
            {description}
          </p>
        </div>

        <div className='mt-6 w-full max-w-[1136px] rounded-[12px] bg-white'>
          <div className='flex flex-col gap-6'>
            <div ref={chartInViewRef} className='mx-auto w-full'>
              {chartInView ? (
                <SeoReportPricePerformanceScatterChart
                  chartConfig={chartConfig}
                  points={normalizedPoints}
                  xTicks={xTicks}
                  yTicks={yTicks}
                  xDomain={xDomain}
                  yDomain={yDomain}
                  yAxisLabel={yAxisLabel}
                  formatMetricTick={formatMetricTick}
                  formatMetricDetail={formatMetricDetail}
                />
              ) : (
                <ScatterChartPlaceholder />
              )}

              <div className='mt-5 flex flex-wrap items-center justify-center gap-5'>
                <div className='flex items-center gap-[7px]'>
                  <span
                    className='size-[10px] rounded-full'
                    style={{ backgroundColor: TOP_PICK_COLOR }}
                  />
                  <p className='text-[13px] font-medium leading-none text-foreground-heading'>
                    {topPickLegendLabel}
                  </p>
                </div>
                <div className='flex items-center gap-[7px]'>
                  <span
                    className='size-[10px] rounded-full'
                    style={{ backgroundColor: OTHER_CANDIDATE_COLOR }}
                  />
                  <p className='text-[13px] font-medium leading-none text-foreground-heading'>
                    {otherCandidatesLegendLabel}
                  </p>
                </div>
              </div>
            </div>

            {summary ? (
              <SeoReportTruncatedCopy
                maxHeight={380}
                className='mx-auto w-full max-w-[900px] text-base leading-7 text-foreground-secondary'
              >
                {typeof summary === 'string'
                  ? renderSeoReportLimitedMarkdown(summary)
                  : summary}
              </SeoReportTruncatedCopy>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}
